/**
 * TRITAL Care® Blog / Resources — central content registry.
 *
 * Single source of truth for the Blog hub + per-article detail pages.
 *
 * TAXONOMY
 * --------
 *   CATEGORIES:       functional topic area (used for filter chips)
 *   AUDIENCES:        who the article is primarily written for
 *                     (used to populate the 4 audience sections)
 *                     employer · clinician · compliance · prn
 *
 * ARTICLE SHAPE
 * -------------
 *   slug, title, excerpt, category, categoryLabel,
 *   audiences (string[]), date, read,
 *   featured?: boolean
 *   body?: (Markdown-ish; only provided for full articles, rest render the CTA template)
 *
 * INTERNAL LINKING STRATEGY
 * -------------------------
 * Each article's body has a `related` array (slugs). We also surface cross-links:
 *   1. Every article links to its category hub on /blog?category=<slug>
 *   2. Compliance articles link to /compliance + /compliance/checklists
 *   3. Employer articles link to /employers + /services
 *   4. Clinician articles link to /clinicians + /compliance/checklists
 *   5. PRN articles link to /shift-connect (marketplace teaser)
 *   6. Featured / hub-level stories anchor topic clusters (hub-and-spoke SEO)
 */

export const CATEGORIES = [
  { slug: "all",                    label: "All" },
  { slug: "workforce-strategy",     label: "Workforce Strategy" },
  { slug: "clinician-career",       label: "Clinician Career" },
  { slug: "compliance-regulatory",  label: "Compliance & Regulatory" },
  { slug: "travel-nursing",         label: "Travel Nursing" },
  { slug: "prn-flexible",           label: "PRN & Flexible" },
  { slug: "industry-insights",      label: "Industry Insights" },
  { slug: "diversity-equity",       label: "Diversity & Equity" },
];

const mk = (o) => ({
  audiences: [],
  read: "8 min read",
  ...o,
  categoryLabel: (CATEGORIES.find((c) => c.slug === o.category) || {}).label || o.category,
});

/* ---------- 20+ articles ---------- */
export const POSTS = [
  /* 1 · Featured flagship */
  mk({
    slug: "msp-vs-vms-2026",
    category: "workforce-strategy",
    audiences: ["employer"],
    title: "MSP vs VMS: what health systems actually need in 2026",
    excerpt:
      "A procurement-grade comparison of managed service programs and vendor management systems — where each model fits, where each breaks, and the hybrid approach that works for most health systems.",
    read: "9 min read", date: "Feb 2026", featured: true,
    body: `
## The short version
MSP = you outsource program management to one vendor. VMS = you centralize requisitions and approvals in a platform while keeping clinical selection decisions in-house. Most health systems benefit from a hybrid — MSP-lite with VMS transparency.

## Where MSPs win
- Fill rates above 92% because the MSP controls the vendor network.
- Consolidated invoicing and credential tracking.
- Strong fit for systems with 10+ facilities or multiple specialties.

## Where VMS-only wins
- Smaller systems with strong internal talent acquisition teams.
- Organizations prioritizing bill-rate transparency over speed.
- Use cases where contract terms must vary per facility.

## TRITAL Care's recommendation
Run MSP for travel + contract roles, VMS-only for local per diem, and an internal float pool for the 10% of shifts that never leave the hospital. We support all three at clinical parity.

## Related playbooks
- **Premium staffing cost benchmark** — see the spend breakdown by model.
- **Supplier diversity in healthcare workforce** — procurement-grade view.
- **Credentialing automation primer** — operator tradeoffs.
    `,
    related: ["premium-staffing-cost-benchmark", "supplier-diversity-healthcare-workforce", "credentialing-automation-primer"],
  }),

  /* 2 */
  mk({
    slug: "travel-nurse-pay-packages",
    category: "clinician-career",
    audiences: ["clinician"],
    title: "Travel nurse pay packages, explained line by line",
    excerpt:
      "Taxable base, non-taxable stipends, overtime blended rate, completion bonuses — how the pieces fit together and how to compare two offers apples to apples.",
    read: "7 min read", date: "Feb 2026",
    body: `
## What's in a travel package
1. **Taxable hourly base** — the number that feeds OT, 401(k), and tax withholdings.
2. **Non-taxable stipends** — housing, meals/incidentals, travel. Only valid if you have a qualifying tax home.
3. **Completion bonus** — paid at contract end if you finish every scheduled shift.
4. **Travel reimbursement** — typically split start / end.

## How to compare offers
- Always compare **total weekly gross**, not hourly rate.
- Ask for a blended OT rate — different agencies use different GSA tables.
- Verify the contract's cancellation and missed-shift policy.
- Confirm housing stipend meets GSA max for the assignment city.

## Use our calculator
TRITAL Care's travel calculator runs the math for you — paste both offers, see the real weekly take-home, and export a PDF for your tax advisor.
    `,
    related: ["first-travel-assignment-30-days", "compact-state-license-roadmap-2026"],
  }),

  /* 3 */
  mk({
    slug: "joint-commission-audit-readiness",
    category: "compliance-regulatory",
    audiences: ["compliance", "employer"],
    title: "Joint Commission audit readiness for staffing partners",
    excerpt:
      "What surveyors actually ask during a staffing-vendor audit — documentation, sampling methodology, and the common findings we see across 200+ facility audits.",
    read: "12 min read", date: "Jan 2026",
    body: `
## What surveyors examine
- **Primary-source verification** for every license / cert within the sampled staff.
- **Orientation records** with competency validation signed off by a facility leader.
- **OSHA and infection-prevention** annual training.
- **Drug-screen and background-check** expiry tracking.

## Top 5 common findings
1. Lapsed ACLS or PALS for ICU / ED travelers.
2. Orientation competency checklists not signed.
3. Drug-screen validity beyond 30-day hire window.
4. Missing state-specific background add-ons.
5. Vendor-maintained OIG and SAM checks without current documentation.

## TRITAL Care's 6-gate onboarding
We enforce six compliance gates before any clinician is released to a facility. Download the full matrix from our compliance hub.
    `,
    related: ["credentialing-automation-primer", "msp-vs-vms-2026"],
  }),

  /* 4 */
  mk({
    slug: "compact-state-license-roadmap-2026",
    category: "travel-nursing",
    audiences: ["clinician"],
    title: "2026 compact-state license roadmap",
    excerpt:
      "States adding compact license recognition, walk-through license timelines, and a clinician-facing framework for planning multi-state careers.",
    read: "6 min read", date: "Jan 2026",
    related: ["travel-nurse-pay-packages", "first-travel-assignment-30-days"],
  }),

  /* 5 */
  mk({
    slug: "premium-staffing-cost-benchmark",
    category: "industry-insights",
    audiences: ["employer"],
    title: "The real cost of premium staffing — and how to reduce it 30%",
    excerpt:
      "A benchmark of bill-rate vs total cost of staffing, with a model-by-model breakdown across travel, per diem, direct-hire, and internal float — and the mix that drives the biggest wins.",
    read: "14 min read", date: "Jan 2026",
    related: ["msp-vs-vms-2026", "prn-fill-economics-playbook"],
  }),

  /* 6 */
  mk({
    slug: "first-travel-assignment-30-days",
    category: "clinician-career",
    audiences: ["clinician"],
    title: "Your first travel assignment: a 30-day checklist",
    excerpt:
      "Housing, license activation, facility onboarding, and compensation setup — a practical playbook for the 30 days before your first travel contract starts.",
    read: "8 min read", date: "Dec 2025",
    related: ["travel-nurse-pay-packages", "compact-state-license-roadmap-2026"],
  }),

  /* 7 */
  mk({
    slug: "supplier-diversity-healthcare-workforce",
    category: "workforce-strategy",
    audiences: ["employer"],
    title: "Supplier diversity in healthcare workforce: the procurement-grade view",
    excerpt:
      "Why MBE/SBE/NMSDC-certified workforce partners show up on the board scorecard — and how to evaluate them at clinical parity, without a diversity-for-diversity's-sake discount.",
    read: "10 min read", date: "Dec 2025",
    related: ["msp-vs-vms-2026", "premium-staffing-cost-benchmark"],
  }),

  /* 8 */
  mk({
    slug: "credentialing-automation-primer",
    category: "compliance-regulatory",
    audiences: ["compliance", "employer"],
    title: "Credentialing automation: a primer for facility operators",
    excerpt:
      "Primary-source verification APIs, expiry monitoring, SOC 2 platform controls, and the tradeoffs facility operators actually navigate — plus a build-vs-buy decision matrix.",
    read: "11 min read", date: "Dec 2025",
    related: ["joint-commission-audit-readiness", "msp-vs-vms-2026"],
  }),

  /* 9 · Employer */
  mk({
    slug: "prn-fill-economics-playbook",
    category: "prn-flexible",
    audiences: ["employer", "prn"],
    title: "PRN fill-rate economics: a playbook for DONs and CNOs",
    excerpt:
      "How to move PRN fill rate from 60% to 90% without growing the premium-labor bill — covering shift-posting cadence, bill-rate math, and the cultural work that unlocks clinician loyalty.",
    read: "10 min read", date: "Nov 2025",
    related: ["premium-staffing-cost-benchmark", "local-per-diem-vs-internal-float"],
  }),

  /* 10 · Employer */
  mk({
    slug: "local-per-diem-vs-internal-float",
    category: "prn-flexible",
    audiences: ["employer", "prn"],
    title: "Local per diem vs internal float: when to build, when to buy",
    excerpt:
      "A decision framework for health systems weighing investment in an internal float pool against outsourcing local per-diem to a partner — with realistic ROI math.",
    read: "9 min read", date: "Nov 2025",
    related: ["prn-fill-economics-playbook", "msp-vs-vms-2026"],
  }),

  /* 11 · Clinician */
  mk({
    slug: "clinician-checklist-best-practices",
    category: "clinician-career",
    audiences: ["clinician"],
    title: "How to complete a skills checklist that actually gets you placed",
    excerpt:
      "Honest self-rating beats a padded checklist every time. A TRITAL Care recruiter walks through the five rating mistakes that cost clinicians assignments.",
    read: "6 min read", date: "Nov 2025",
    related: ["travel-nurse-pay-packages", "first-travel-assignment-30-days"],
  }),

  /* 12 · Clinician */
  mk({
    slug: "ccrn-ccrn-pa-recertification-2026",
    category: "clinician-career",
    audiences: ["clinician"],
    title: "CCRN / CCRN-PA recertification: 2026 changes and CE plan",
    excerpt:
      "AACN's 2026 specialty cert updates, CE credits accepted, and a 12-month plan that fits into a travel or PRN schedule without burning out.",
    read: "7 min read", date: "Oct 2025",
    related: ["clinician-checklist-best-practices", "travel-nurse-pay-packages"],
  }),

  /* 13 · Compliance */
  mk({
    slug: "cms-staffing-mandate-snf-2026",
    category: "compliance-regulatory",
    audiences: ["compliance", "employer"],
    title: "CMS SNF staffing mandate: an implementation roadmap",
    excerpt:
      "CMS's minimum staffing rule for SNFs takes effect in phases. A practical roadmap for administrators and ADONs on RN-around-the-clock, HPRD targets, and exception requests.",
    read: "12 min read", date: "Oct 2025",
    related: ["joint-commission-audit-readiness", "credentialing-automation-primer"],
  }),

  /* 14 · Compliance */
  mk({
    slug: "hospital-at-home-compliance-playbook",
    category: "compliance-regulatory",
    audiences: ["compliance", "employer"],
    title: "Hospital-at-home: the compliance playbook operators miss",
    excerpt:
      "H@H is growing — and so is the regulatory tailwind. What an acute care hospital-at-home program requires for CMS Acute Hospital Care at Home waiver compliance.",
    read: "11 min read", date: "Oct 2025",
    related: ["cms-staffing-mandate-snf-2026", "credentialing-automation-primer"],
  }),

  /* 15 · Industry / Insights */
  mk({
    slug: "rural-health-workforce-gap-2026",
    category: "industry-insights",
    audiences: ["employer", "clinician"],
    title: "Rural health's workforce gap, visualized",
    excerpt:
      "Using HRSA and AHA data, we map the US counties with the sharpest RN supply deficits and propose three workforce strategies that have worked in similar regions.",
    read: "8 min read", date: "Sep 2025",
    related: ["supplier-diversity-healthcare-workforce", "msp-vs-vms-2026"],
  }),

  /* 16 · Industry */
  mk({
    slug: "ai-in-healthcare-recruiting",
    category: "industry-insights",
    audiences: ["employer"],
    title: "AI in healthcare recruiting: what's hype, what's working in 2026",
    excerpt:
      "A measured look at generative AI applications in recruiting — resume parsing, screening, and match-quality gains — and the integrity guardrails a staffing partner should commit to.",
    read: "10 min read", date: "Sep 2025",
    related: ["credentialing-automation-primer", "premium-staffing-cost-benchmark"],
  }),

  /* 17 · Travel */
  mk({
    slug: "irs-tax-home-travel-nurses",
    category: "travel-nursing",
    audiences: ["clinician"],
    title: "IRS tax-home rules for travel nurses: a compliance checklist",
    excerpt:
      "What qualifies as a tax home, how to document it, and the three most common audit triggers for travel clinicians — plus a TRITAL Care-recommended CPA network.",
    read: "9 min read", date: "Aug 2025",
    related: ["travel-nurse-pay-packages", "compact-state-license-roadmap-2026"],
  }),

  /* 18 · PRN */
  mk({
    slug: "prn-app-selection-for-facilities",
    category: "prn-flexible",
    audiences: ["employer", "prn"],
    title: "Selecting a PRN app for your facility: the due-diligence checklist",
    excerpt:
      "Fill rate, credential rigor, bill-rate transparency, W-2 vs 1099 exposure, and SLA teeth — the questions procurement teams should be asking every PRN-platform vendor.",
    read: "8 min read", date: "Aug 2025",
    related: ["prn-fill-economics-playbook", "local-per-diem-vs-internal-float"],
  }),

  /* 19 · Diversity */
  mk({
    slug: "language-access-clinician-staffing",
    category: "diversity-equity",
    audiences: ["employer", "clinician"],
    title: "Language-concordant staffing: patient outcomes, not just optics",
    excerpt:
      "Research on bilingual clinicians and patient outcomes, plus a practical sourcing playbook for language-concordant travel, per diem, and direct-hire assignments.",
    read: "7 min read", date: "Jul 2025",
    related: ["supplier-diversity-healthcare-workforce", "rural-health-workforce-gap-2026"],
  }),

  /* 20 · Diversity */
  mk({
    slug: "veteran-clinicians-transition-pathways",
    category: "diversity-equity",
    audiences: ["clinician", "employer"],
    title: "Veteran clinicians: transition pathways into civilian staffing",
    excerpt:
      "From corpsman to civilian RN, and military MD to hospitalist — the credential translation, license application, and employer-partner playbook for veteran clinicians.",
    read: "8 min read", date: "Jul 2025",
    related: ["first-travel-assignment-30-days", "compact-state-license-roadmap-2026"],
  }),

  /* 21 · Clinician */
  mk({
    slug: "burnout-prevention-travel-clinician",
    category: "clinician-career",
    audiences: ["clinician"],
    title: "Burnout prevention on a travel schedule",
    excerpt:
      "Rest-cycle planning between assignments, therapy access in a new state, and the workload red flags that should drive an early contract conversation.",
    read: "6 min read", date: "Jul 2025",
    related: ["first-travel-assignment-30-days", "ccrn-ccrn-pa-recertification-2026"],
  }),

  /* 22 · Employer */
  mk({
    slug: "internal-float-pool-blueprint",
    category: "workforce-strategy",
    audiences: ["employer"],
    title: "Internal float pool blueprint: from charter to launch in 90 days",
    excerpt:
      "Every step of standing up an internal float pool — from governance and pay differentials to scheduling systems and clinician recruitment — with a 90-day Gantt.",
    read: "13 min read", date: "Jun 2025",
    related: ["local-per-diem-vs-internal-float", "premium-staffing-cost-benchmark"],
  }),
];

/* ---------- Lookup helpers ---------- */
export function getPostBySlug(slug) {
  return POSTS.find((p) => p.slug === slug) || null;
}

export function getRelated(slugs = []) {
  return slugs.map((s) => getPostBySlug(s)).filter(Boolean);
}

export function getByAudience(audience, limit = 3) {
  return POSTS.filter((p) => p.audiences.includes(audience)).slice(0, limit);
}

export function getByCategory(categorySlug) {
  if (!categorySlug || categorySlug === "all") return POSTS;
  return POSTS.filter((p) => p.category === categorySlug);
}

/* ---------- Audience sections ---------- */
export const AUDIENCE_SECTIONS = [
  {
    key: "employer",
    eyebrow: "For Employers",
    title: "Workforce strategy for health systems & facilities",
    blurb:
      "Procurement-grade playbooks on MSP vs VMS, bill-rate economics, float-pool design, and supplier diversity — written for DONs, CNOs, and workforce teams.",
    cta: { label: "Employer services", href: "/employers" },
  },
  {
    key: "clinician",
    eyebrow: "For Clinicians",
    title: "Career, credentials & assignment playbooks",
    blurb:
      "Travel-comp math, license and recertification roadmaps, burnout prevention, and the self-rating tactics that actually land the next assignment.",
    cta: { label: "Clinician pathways", href: "/clinicians" },
  },
  {
    key: "compliance",
    eyebrow: "For Compliance & Regulatory",
    title: "Joint Commission, CMS, and credentialing guides",
    blurb:
      "What surveyors actually ask, CMS staffing mandates, H@H waivers, and the credentialing-automation patterns that keep facilities audit-ready.",
    cta: { label: "Compliance hub", href: "/compliance" },
  },
  {
    key: "prn",
    eyebrow: "PRN & Flexible Staffing",
    title: "Per-diem, internal float & gig-marketplace insights",
    blurb:
      "Fill-rate economics, per-diem vs internal-float decision frameworks, and the due-diligence every facility should run before signing a PRN-platform contract.",
    cta: { label: "Explore TRITAL ShiftBridge™", href: "/shift-connect" },
  },
];

/* ---------- FAQ (hub-level) ---------- */
export const HUB_FAQS = [
  {
    q: "How often is the Resources hub updated?",
    a: "We publish 2–3 long-form briefs per month. Subscribe below to get each one in your inbox the morning it ships.",
  },
  {
    q: "Can I republish your articles?",
    a: "Yes — with attribution and a link back to the original. Email media@tritalcare.com and we'll send a clean-quote PDF for print reuse.",
  },
  {
    q: "Do you accept guest contributions?",
    a: "We accept contributed pieces from practicing clinicians, compliance leads, and workforce operators. Pitch at editor@tritalcare.com with a 100-word outline.",
  },
  {
    q: "Are these resources vendor-neutral?",
    a: "Every brief is written to be usable whether you engage TRITAL Care or not. Where we reference our own services, it's clearly marked — not buried in the copy.",
  },
];
